import { css } from '@emotion/css';

/**
 * Horizontal padding inside a cell.
 *
 * Load-bearing, not cosmetic: a block's drag handle renders at roughly
 * `[blockLeft - 18, blockLeft - 2]`. The cell has to leave that much room to
 * its left or the handle lands outside the cell and nothing inside a cell can
 * be dragged — silently, with no error. See the COLUMN_GAP note in CLAUDE.md
 * for the same trap in the columns block.
 */
export const CELL_PADDING_X = 24;
const CELL_PADDING_Y = 6;

/**
 * Column widths are absolute pixels, not shares of the page.
 *
 * A ratio model made the table always fill the page and made every resize a
 * tug of war — shrinking one column grew its neighbours. Pixels mean a drag
 * moves one boundary and the table itself gets wider or narrower, which is
 * what `affine:table` does and what people expect.
 */
export const DEFAULT_COLUMN_WIDTH = 180;
export const MIN_COLUMN_WIDTH = 60;

/** Width of the resizer's grab strip, centred on a column boundary. */
export const RESIZER_HIT_WIDTH = 10;

const BORDER = '1px solid var(--affine-border-color, #e3e2e4)';

/** Room to the right and below the grid for the add-row/column strips. */
export const ADD_BUTTON_SIZE = 16;
const ADD_BUTTON_GAP = 2;

/** Room above and left of the grid for the row/column handles. */
const HANDLE_CLEARANCE = 12;

/** Vertical rhythm, matching what other blocks in a note use. */
const BLOCK_MARGIN = 12;

export const richTableHostStyles = css({
  display: 'block',
  overflowX: 'auto',
  overscrollBehaviorX: 'contain',
  // `overflow-x: auto` clips anything outside this box, and every control
  // sits outside the grid: the add strips to the right and below, the ··· and
  // ⋮ handles above and to the left. Reserve room for all four or they are
  // invisible and unclickable.
  paddingTop: `${HANDLE_CLEARANCE}px`,
  paddingLeft: `${HANDLE_CLEARANCE}px`,
  paddingRight: `${ADD_BUTTON_SIZE + ADD_BUTTON_GAP}px`,
  paddingBottom: `${ADD_BUTTON_SIZE + ADD_BUTTON_GAP}px`,
  // ...and pull the box back by the same amount, so the *grid* lines up with
  // the block above it instead of sitting a clearance-width further in.
  marginTop: `${BLOCK_MARGIN - HANDLE_CLEARANCE}px`,
  marginLeft: `${-HANDLE_CLEARANCE}px`,
  marginBottom: `${BLOCK_MARGIN}px`,
});

export const richTableWrapperStyles = css({
  position: 'relative',
  // Hugs the grid so the add-row/column strips, which are positioned against
  // this element, sit against the table's real edges rather than the page's.
  width: 'max-content',
  maxWidth: '100%',
});

const addButtonBase = {
  position: 'absolute' as const,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  fontSize: '16px',
  borderRadius: '2px',
  opacity: 0,
  color: 'var(--affine-icon-secondary, #8e8d91)',
  backgroundColor: 'var(--affine-hover-color, rgba(0,0,0,0.04))',
  transition: 'opacity 0.2s ease-in-out, background-color 0.2s ease-in-out',
  ':hover': {
    opacity: 1,
    color: 'var(--affine-icon-color, #7389fd)',
    backgroundColor: 'var(--affine-hover-color-filled, rgba(0,0,0,0.08))',
  },
};

export const addColumnButtonStyles = css({
  ...addButtonBase,
  cursor: 'col-resize',
  top: 0,
  left: `calc(100% + ${ADD_BUTTON_GAP}px)`,
  width: `${ADD_BUTTON_SIZE}px`,
  height: '100%',
});

export const addRowButtonStyles = css({
  ...addButtonBase,
  cursor: 'row-resize',
  top: `calc(100% + ${ADD_BUTTON_GAP}px)`,
  left: 0,
  width: '100%',
  height: `${ADD_BUTTON_SIZE}px`,
});

export const addBothButtonStyles = css({
  ...addButtonBase,
  cursor: 'nwse-resize',
  top: `calc(100% + ${ADD_BUTTON_GAP}px)`,
  left: `calc(100% + ${ADD_BUTTON_GAP}px)`,
  width: `${ADD_BUTTON_SIZE}px`,
  height: `${ADD_BUTTON_SIZE}px`,
});

/** The ··· above a column and the ⋮ left of a row. */
const handleBase = {
  position: 'absolute' as const,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  cursor: 'pointer',
  borderRadius: '4px',
  color: 'var(--affine-icon-secondary, #8e8d91)',
  backgroundColor: 'var(--affine-white, #fff)',
  border: '1px solid var(--affine-border-color, #e3e2e4)',
  boxShadow: 'var(--affine-shadow-1, 0 0 4px rgba(0,0,0,0.14))',
  zIndex: 2,
  ':hover': {
    color: 'var(--affine-icon-color, #7389fd)',
  },
};

export const columnHandleStyles = css({
  ...handleBase,
  top: '-11px',
  left: '50%',
  transform: 'translateX(-50%)',
  width: '28px',
  height: '14px',
});

export const rowHandleStyles = css({
  ...handleBase,
  left: '-11px',
  top: '50%',
  transform: 'translateY(-50%)',
  width: '14px',
  height: '28px',
});

export const richTableGridStyles = css({
  display: 'grid',
  position: 'relative',
  borderTop: BORDER,
  borderLeft: BORDER,
  // Sized by its columns, not by the page. The host scrolls when the sum
  // exceeds the available width.
  width: 'max-content',
  maxWidth: 'none',
});

/**
 * Rows are `display: contents` so their cells become grid items of the table
 * itself — that is what keeps columns aligned across rows.
 */
export const richTableRowStyles = css({
  display: 'contents',
});

export const richTableCellStyles = css({
  display: 'block',
  position: 'relative',
  minWidth: 0,
  overflowWrap: 'anywhere',
  padding: `${CELL_PADDING_Y}px ${CELL_PADDING_X}px`,
  borderRight: BORDER,
  borderBottom: BORDER,
});

export const richTableHeaderCellStyles = css({
  background: 'var(--affine-hover-color, rgba(0,0,0,0.04))',
  fontWeight: 600,
});

export const richTableResizerStyles = css({
  position: 'absolute',
  top: 0,
  bottom: 0,
  width: `${RESIZER_HIT_WIDTH}px`,
  right: `${-RESIZER_HIT_WIDTH / 2}px`,
  // The last column's handle sits wholly inside the cell so it cannot overlap
  // the add-column strip parked just outside the grid.
  '&[data-last]': {
    right: 0,
  },
  cursor: 'col-resize',
  // Below the drag-handle overlay on purpose: where the two overlap the handle
  // wins, and the resizer stays grabbable above and below it.
  zIndex: 0,
  touchAction: 'none',
});

export const richTableResizerLineStyles = css({
  position: 'absolute',
  top: 0,
  bottom: 0,
  left: '50%',
  width: '2px',
  transform: 'translateX(-50%)',
  background: 'transparent',
  transition: 'background 0.15s ease',
  '.affine-rich-table-resizer:hover &, .affine-rich-table-resizer[data-dragging] &':
    {
      background: 'var(--affine-primary-color, #1e96eb)',
    },
});

/** Insertion marker shown while dragging a row or column handle. */
const reorderMarkerBase = {
  position: 'absolute' as const,
  background: 'var(--affine-primary-color, #1e96eb)',
  pointerEvents: 'none' as const,
  zIndex: 3,
};

export const columnReorderMarkerStyles = css({
  ...reorderMarkerBase,
  top: 0,
  bottom: 0,
  width: '2px',
});

export const rowReorderMarkerStyles = css({
  ...reorderMarkerBase,
  left: 0,
  right: 0,
  height: '2px',
});

/** The column or row being dragged, dimmed so the marker reads clearly. */
export const reorderingSourceStyles = css({
  opacity: 0.45,
});
